import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { CheckCircle2, AlertCircle, Info, X } from "lucide-react";

export type ToastVariant = "success" | "error" | "info";

export interface ToastItem {
  id: number;
  variant: ToastVariant;
  message: string;
}

interface ToastContextValue {
  show: (message: string, variant?: ToastVariant) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const idRef = useRef(1);

  const show = useCallback((message: string, variant: ToastVariant = "info") => {
    const id = idRef.current++;
    setItems((prev) => [...prev, { id, variant, message }]);
    window.setTimeout(() => {
      setItems((prev) => prev.filter((t) => t.id !== id));
    }, 4000);
  }, []);

  const value = useMemo<ToastContextValue>(
    () => ({
      show,
      success: (m) => show(m, "success"),
      error: (m) => show(m, "error"),
      info: (m) => show(m, "info"),
    }),
    [show]
  );

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Toaster
        items={items}
        onDismiss={(id) => setItems((p) => p.filter((t) => t.id !== id))}
      />
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error("useToast must be used inside ToastProvider");
  return ctx;
}

function Toaster({
  items,
  onDismiss,
}: {
  items: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  return (
    <div className="pointer-events-none fixed top-4 right-4 z-50 flex w-full max-w-sm flex-col gap-2">
      {items.map((t) => (
        <ToastView key={t.id} toast={t} onDismiss={() => onDismiss(t.id)} />
      ))}
    </div>
  );
}

function ToastView({
  toast,
  onDismiss,
}: {
  toast: ToastItem;
  onDismiss: () => void;
}) {
  const [entered, setEntered] = useState(false);
  useEffect(() => {
    const id = window.setTimeout(() => setEntered(true), 10);
    return () => window.clearTimeout(id);
  }, []);

  const meta: Record<
    ToastVariant,
    { Icon: typeof CheckCircle2; iconColor: string; ring: string }
  > = {
    success: {
      Icon: CheckCircle2,
      iconColor: "text-emerald-600",
      ring: "ring-emerald-100",
    },
    error: {
      Icon: AlertCircle,
      iconColor: "text-red-600",
      ring: "ring-red-100",
    },
    info: {
      Icon: Info,
      iconColor: "text-brand-600",
      ring: "ring-brand-100",
    },
  };
  const m = meta[toast.variant];
  const Icon = m.Icon;

  return (
    <div
      className={`pointer-events-auto flex items-start gap-3 rounded-xl border border-ink-200/70 bg-white px-3.5 py-3 shadow-xl ring-4 ${m.ring} transition-all duration-300 ease-out ${
        entered
          ? "translate-x-0 opacity-100"
          : "translate-x-6 opacity-0 scale-95"
      }`}
      role="status"
    >
      <Icon className={`mt-0.5 h-5 w-5 flex-shrink-0 ${m.iconColor}`} />
      <p className="flex-1 pt-0.5 text-sm leading-snug text-ink-900">
        {toast.message}
      </p>
      <button
        onClick={onDismiss}
        className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-md text-ink-400 transition-colors hover:bg-ink-100 hover:text-ink-700"
        aria-label="Dismiss"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
